import React, { type ReactNode, forwardRef, useState, type MouseEvent } from 'react';
import Link from '@atlaskit/link';
import { type UIAnalyticsEvent, AnalyticsListener } from '@atlaskit/analytics-next';
import type { ButtonProps } from '@atlaskit/button';
import type { PopupComponentProps } from '@atlaskit/popup';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import AiOptIn from '@atlassian/ai-opt-in';
import { expVal } from '@atlassian/jira-feature-experiments';
import AiEnableEndUser from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/components/ai-enable-banner-end-user/index.tsx';
import AiEnableOrgAdmin from '@atlassian/jira-illustrations/src/ui/adg4/jira/other/components/ai-enable-banner-org-admin/index.tsx';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	fireScreenAnalytics,
	SCREEN,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { LEARN_MORE_URL } from '../../constants.tsx';
import { AIButton } from '../ai-button/index.tsx';
import messages from './messages.tsx';

const AIPopupContainerComponent = forwardRef<HTMLDivElement, PopupComponentProps>(
	({ children, ...props }, ref) => (
		<div {...props} ref={ref}>
			{children}
		</div>
	),
);

type AIUnavailable = {
	aiAvailable: false;
	aiEnabled: false;
};
type AiAvailable = {
	aiAvailable: true;
	aiEnabled: true;
};
type AiDisabledOrgAdmin = {
	aiAvailable: true;
	aiEnabled: false;
	isOrgAdmin: true;
	orgAdminLink: string;
};
type AiDisabledEndUser = {
	aiAvailable: true;
	aiEnabled: false;
	isOrgAdmin: false;
	orgAdminLink: null;
};

export type AiState = AiAvailable | AIUnavailable | AiDisabledOrgAdmin | AiDisabledEndUser;

type PopupButtonProps = ButtonProps & {
	autoFocus?: boolean;
	onClick: (_: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
	analyticsSource: string;
	analyticsEventName: string;
	aiState: AiState;
};

export const AIButtonPopup = ({
	onClick,
	autoFocus,
	analyticsSource,
	aiState,
	analyticsEventName,
}: PopupButtonProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (!aiState.aiAvailable) {
		return null;
	}

	if (aiState.aiEnabled) {
		return <AIButton onClick={onClick} autoFocus={autoFocus} />;
	}
	const { isOrgAdmin, orgAdminLink } = aiState;

	const aiModalState = isOrgAdmin ? 'adminOptIn' : 'endUserOptIn';

	const getFormattedMessage = (messageObj: MessageDescriptor) =>
		formatMessage(messageObj, {
			learn_more_link: (message): ReactNode =>
				fg('replace-anchor-with-link__gravity-ai') ? (
					<Link
						target="_blank"
						onClick={() =>
							fireUIAnalytics(
								createAnalyticsEvent({}),
								'link clicked',
								'atassianIntelligenceLearnMore',
								{ aiAction: aiModalState },
							)
						}
						href={LEARN_MORE_URL}
					>
						{message}
					</Link>
				) : (
					<a
						target="_blank"
						onClick={() =>
							fireUIAnalytics(
								createAnalyticsEvent({}),
								'link clicked',
								'atassianIntelligenceLearnMore',
								{ aiAction: aiModalState },
							)
						}
						href={LEARN_MORE_URL}
					>
						{message}
					</a>
				),
			// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
			p: (message): ReactNode => <p>{message}</p>,
			br: <br />,
		});

	const handleEvent = (e: UIAnalyticsEvent) => {
		const payload = e.payload || {};
		const payloadData = payload.data || {};

		// Pipe sendUIEvent type events as UI analytics
		if (
			payload.type === 'sendUIEvent' &&
			payloadData.action &&
			payloadData.actionSubject &&
			payloadData.actionSubjectId
		) {
			fireUIAnalytics(
				e,
				`${payloadData.actionSubject} ${payloadData.action}`,
				payloadData.actionSubjectId,
			);
		}

		// Pipe sendScreenEvent type events as screen analytics
		if (payload.type === 'sendScreenEvent') {
			fireScreenAnalytics(e);
		}
	};

	return (
		<Popup
			messageId="atlassian-intelligence.common.ui.ai-button-popup.popup.ai-opt-in"
			messageType="marketing"
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			popupComponent={AIPopupContainerComponent}
			content={() => (
				<ContextualAnalyticsData
					sourceName={`${analyticsSource}AiOptInDialog`}
					sourceType={SCREEN}
					attributes={{
						isOrgAdmin,
					}}
				>
					<AnalyticsListener channel="fabric-elements" onEvent={handleEvent}>
						<AiOptIn
							source={analyticsSource}
							product="jira"
							isOrgAdmin={isOrgAdmin}
							image={
								isOrgAdmin ? (
									<AiEnableOrgAdmin alt={formatMessage(messages.aiOptInAltMessage)} />
								) : (
									<AiEnableEndUser alt={formatMessage(messages.aiOptInAltMessage)} />
								)
							}
							testId="atlassian-intelligence.common.ui.ai-button-popup.ai-opt-in"
							onAdminClose={() => setIsOpen(false)}
							onAdminCTA={() => setIsOpen(false)}
							adminCTAText={formatMessage(messages.adminButton)}
							adminCloseCTAText={formatMessage(messages.dismissButton)}
							adminLink={orgAdminLink || undefined} // || undefined because the internal types of AiOptIn don't accept null :poop:
							headerText={formatMessage(messages.heading)}
							adminHeaderText={formatMessage(messages.heading)}
							bodyText={getFormattedMessage(
								expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
									? messages.userDescriptionIssueTermRefresh
									: messages.userDescription,
							)}
							adminBodyText={getFormattedMessage(
								expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
									? messages.adminDescriptionIssueTermRefresh
									: messages.adminDescription,
							)}
						/>
					</AnalyticsListener>
				</ContextualAnalyticsData>
			)}
			trigger={(triggerProps) => (
				<AIButton
					{...triggerProps}
					onClick={(_, analyticsEvent) => {
						fireUIAnalytics(analyticsEvent, 'button clicked', analyticsEventName, {
							aiAction: aiModalState,
						});
						setIsOpen((open) => !open);
					}}
					autoFocus={autoFocus}
				/>
			)}
			placement="bottom-start"
		/>
	);
};
